import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowLeft, ArrowRight, Loader2, ChevronDown, ChevronRight,
  Table2, BarChart3, Hash, Type, Percent,
} from 'lucide-react';
import { onboardingApi } from '../../services/api';
import type { OnboardingState, ProfileResult, TableProfile } from '../../types';

interface Props {
  state: OnboardingState;
  setProfileResult: (r: ProfileResult) => void;
  onNext: () => void;
  onBack: () => void;
}

const ProfileStep: React.FC<Props> = ({ state, setProfileResult, onNext, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState('');
  // Track which cards the user has COLLAPSED. Empty set = everything
  // expanded by default (no clicking needed to see the results).
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const started = useRef(false);

  /* Auto-start profiling when step is reached */
  useEffect(() => {
    if (started.current || state.profileResult) return;
    started.current = true;

    const runProfiling = async () => {
      setLoading(true);
      setError('');
      setProgress('Connecting to database and profiling selected tables...');
      try {
        const resp = await onboardingApi.profileSource({
          connection: state.connection,
          schema: state.selectedSchema,
          tables: state.selectedTables.map((t) => t.table_name),
          sample_size: 10000,
        });
        setProfileResult(resp.data.payload);
        setProgress('');
      } catch (err: any) {
        setError(err.response?.data?.message || 'Profiling failed');
      } finally {
        setLoading(false);
      }
    };

    runProfiling();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const result = state.profileResult;

  return (
    <div>
      <h2 className="text-lg font-semibold text-cargo-text mb-1">Profile The Source Table</h2>
      <p className="text-cargo-muted text-sm mb-4">
        Column-level profiling for {state.selectedTables.length} selected table{state.selectedTables.length !== 1 ? 's' : ''} in <strong>{state.selectedSchema}</strong>
      </p>

      {/* Loading state */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-16 gap-4">
          <Loader2 className="w-8 h-8 text-cargo-accent animate-spin" />
          <p className="text-cargo-muted text-sm">{progress}</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-cargo-error/10 border border-cargo-error/30 rounded-lg p-4 text-cargo-error text-sm mb-4">
          {error}
          <button
            onClick={() => { started.current = false; setError(''); }}
            className="ml-3 underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-3">
          {/* Summary bar */}
          <div className="flex gap-4 mb-4">
            <StatBadge label="Tables" value={result.tables.length} />
            <StatBadge
              label="Total Rows"
              value={result.tables.reduce((s, t) => s + t.row_count, 0).toLocaleString()}
            />
            <StatBadge
              label="Total Columns"
              value={result.tables.reduce((s, t) => s + t.columns.length, 0)}
            />
          </div>

          {/* Per-table expandable cards */}
          {result.tables.map((table) => (
            <TableCard
              key={table.table_name}
              table={table}
              expanded={!collapsed.has(table.table_name)}
              onToggle={() =>
                setCollapsed((prev) => {
                  const next = new Set(prev);
                  if (next.has(table.table_name)) next.delete(table.table_name);
                  else next.add(table.table_name);
                  return next;
                })
              }
            />
          ))}
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-cargo-muted hover:text-cargo-text transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button
          onClick={onNext}
          disabled={!result}
          className="flex items-center gap-2 bg-cargo-accent hover:bg-cargo-accent-hover text-white
                     font-medium py-2.5 px-5 rounded-lg transition-colors disabled:opacity-30"
        >
          Next <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/* ─── Sub-components ─── */

function StatBadge({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-cargo-surface border border-cargo-border rounded-lg px-4 py-2">
      <p className="text-xs text-cargo-muted">{label}</p>
      <p className="text-lg font-semibold text-cargo-text">{value}</p>
    </div>
  );
}

function TableCard({
  table,
  expanded,
  onToggle,
}: {
  table: TableProfile;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="bg-cargo-surface border border-cargo-border rounded-xl overflow-hidden">
      {/* Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-3 hover:bg-cargo-hover/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Table2 className="w-4 h-4 text-cargo-accent" />
          <span className="font-medium text-cargo-text">{table.table_name}</span>
          <span className="text-xs text-cargo-muted">
            {table.row_count.toLocaleString()} rows | {table.columns.length} columns |
            sampled {table.sampled_rows.toLocaleString()}
          </span>
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-cargo-muted" />
        ) : (
          <ChevronRight className="w-4 h-4 text-cargo-muted" />
        )}
      </button>

      {/* Expanded column details */}
      {expanded && (
        <div className="border-t border-cargo-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cargo-panel text-cargo-muted text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-2">Column</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-right px-4 py-2">Null %</th>
                <th className="text-right px-4 py-2">Unique %</th>
                <th className="text-left px-4 py-2">Stats</th>
                <th className="text-left px-4 py-2">Samples</th>
              </tr>
            </thead>
            <tbody>
              {table.columns.map((col) => {
                const p = col.profiling;
                const isNumeric = p.min !== null && p.min !== undefined;
                return (
                  <tr
                    key={col.column_name}
                    className="border-t border-cargo-border hover:bg-cargo-hover/20"
                  >
                    <td className="px-4 py-2 font-medium text-cargo-text flex items-center gap-2">
                      {isNumeric ? (
                        <Hash className="w-3 h-3 text-blue-400" />
                      ) : (
                        <Type className="w-3 h-3 text-green-400" />
                      )}
                      {col.column_name}
                    </td>
                    <td className="px-4 py-2 text-cargo-muted">{col.data_type}</td>
                    <td className="px-4 py-2 text-right">
                      <NullBadge pct={p.null_percentage} />
                    </td>
                    <td className="px-4 py-2 text-right text-cargo-text">
                      {p.unique_percentage.toFixed(1)}%
                    </td>
                    <td className="px-4 py-2 text-cargo-muted text-xs">
                      {isNumeric ? (
                        <span>
                          min={p.min} max={p.max} mean={p.mean?.toFixed(1)}
                        </span>
                      ) : p.min_length !== null ? (
                        <span>
                          len: {p.min_length}–{p.max_length} avg={p.avg_length?.toFixed(1)}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-2 text-cargo-muted text-xs max-w-[200px] truncate">
                      {p.sample_values?.slice(0, 3).join(', ') || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NullBadge({ pct }: { pct: number }) {
  const color =
    pct === 0
      ? 'text-cargo-success'
      : pct < 5
      ? 'text-cargo-warning'
      : 'text-cargo-error';
  return <span className={`font-medium ${color}`}>{pct.toFixed(1)}%</span>;
}

export default ProfileStep;
